import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Network, Search } from 'lucide-react';
import { guaranteesAPI } from 'services/swagger-api';
import { InputText } from 'components/forms/InputText';
import { EmptyState } from 'components/ui/EmptyState';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BranchDerivedPremiumsCard } from './BranchDerivedPremiumsCard';
import { GuaranteeRows } from './GuaranteeRows';
import { useBranchesResource } from './useBranches';
import { useGuaranteeActions } from './useGuaranteeActions';

/**
 * Garanties d'une branche : le socle que ses tunnels peuvent proposer.
 *
 * La recherche est locale — une branche porte quelques dizaines de garanties, les
 * filtrer côté serveur ajouterait un aller-retour sans rien apporter.
 */
export const BranchGuarantees: FC = () => {
  const { brancheId } = useParams<{ brancheId: string }>();
  const navigate = useNavigate();
  const { branches, reload: reloadBranches } = useBranchesResource();
  const branch = useMemo(
    () => branches.find((item) => item.id === brancheId),
    [branches, brancheId]
  );
  const { createMenuItem } = useActionMenu();
  const [search, setSearch] = useState('');

  const fetchGuarantees = useCallback(
    () => guaranteesAPI.getGuaranteesByBranch({ brancheId: brancheId as string }),
    [brancheId]
  );
  const { data, isLoading, error, reload } = useAsyncResource(
    fetchGuarantees,
    Boolean(brancheId)
  );

  // Une garantie modifiée peut changer de rôle dérivé : la branche, qui résume ces rôles,
  // est rechargée avec la liste.
  const reloadAll = useCallback(
    () => Promise.all([reload(), reloadBranches()]).then(() => undefined),
    [reload, reloadBranches]
  );

  const actions = useGuaranteeActions({ branches, branchId: brancheId, onChanged: reloadAll });

  const guarantees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...(data ?? [])]
      .filter(
        (guarantee) =>
          !term ||
          [guarantee.code, guarantee.shortLabel, guarantee.label].some((field) =>
            (field ?? '').toLowerCase().includes(term)
          )
      )
      .sort((a, b) => (a.shortLabel ?? '').localeCompare(b.shortLabel ?? '', 'fr'));
  }, [data, search]);

  if (!brancheId) {
    return (
      <EmptyState
        icon={<Network className="h-8 w-8 text-slate-400" />}
        title="Branche introuvable"
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate('/back-office/branches')}
        className="inline-flex w-fit items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary"
      >
        <ArrowLeft size={16} />
        Retour aux branches
      </button>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-bold text-zinc-800">{branch?.label ?? 'Branche'}</h1>
        {branch?.code && (
          <span className="rounded bg-white px-2 py-0.5 font-mono text-xs text-gray-500 shadow-sm">
            {branch.code}
          </span>
        )}
      </div>

      {branch && <BranchDerivedPremiumsCard branch={branch} onSaved={reloadAll} />}

      <BlockCard
        title="Garanties de la branche"
        description="Socle proposé aux tunnels de cette branche"
        icon={<Network className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {data?.length ?? 0}
          </span>
        }
        actions={[
          createMenuItem('add', 'Créer une garantie', () => actions.openGuarantee(), {
            icon: 'add',
          }),
        ]}
        isLoading={isLoading}
        error={error}
        isEmpty={(data ?? []).length === 0}
        emptyLabel="Aucune garantie sur cette branche"
        flushBody
      >
        <div className="border-b border-gray-100 px-5 py-3">
          <InputText
            name="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer par code ou libellé"
            aria-label="Filtrer les garanties"
          />
        </div>

        {guarantees.length === 0 ? (
          <p className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400">
            <Search className="h-4 w-4" />
            Aucune garantie ne correspond à « {search} »
          </p>
        ) : (
          <GuaranteeRows
            guarantees={guarantees}
            onEdit={actions.openGuarantee}
            onDelete={actions.requestDelete}
            branch={branch}
          />
        )}
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(actions.guaranteeToDelete)}
        onClose={actions.cancelDelete}
        onConfirm={actions.confirmDelete}
        isLoading={actions.isDeleting}
        title="Supprimer cette garantie ?"
        description={`« ${actions.guaranteeToDelete?.shortLabel ?? ''} » sera retirée du référentiel. Les tunnels qui la proposent ne pourront plus s'y référer.`}
        confirmLabel="Supprimer"
      />
    </div>
  );
};

export default BranchGuarantees;
